import type { InferContractRouterOutputs } from '@orpc/contract'
import { useQuery } from '@tanstack/react-query'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { orpc } from './contract'

export type ModelFeatures =
  InferContractRouterOutputs<BaseContract>['generationModel']['getFeaturesByModelVersionId']['features']
export type ModelFeature = ModelFeatures[number]
export type ModelFeatureName = ModelFeature['featureName']

export function useModelFeatures(modelVersionId?: string) {
  const enabled = !!modelVersionId
  return useQuery({
    ...orpc.generationModel.getFeaturesByModelVersionId.queryOptions({
      input: { modelVersionId: modelVersionId ?? '' },
    }),
    enabled,
  })
}

export function getModelFeature(
  features: ModelFeatures | undefined,
  featureName: ModelFeatureName,
) {
  return features?.find(feature => feature.featureName === featureName)
}

export function isModelFeatureEnabled(
  features: ModelFeatures | undefined,
  featureName: ModelFeatureName,
  defaultEnabled = false,
): boolean {
  const feature = getModelFeature(features, featureName)
  if (!feature) return defaultEnabled
  return feature.status === 'on'
}

export function isModelFeatureExplicitlyDisabled(
  features: ModelFeatures | undefined,
  featureName: ModelFeatureName,
): boolean {
  return getModelFeature(features, featureName)?.status === 'off'
}

export function useModelFeatureFlag(
  modelVersionId: string | undefined,
  featureName: ModelFeatureName,
  defaultEnabled = false,
) {
  const query = useModelFeatures(modelVersionId)
  const features = query.data?.features
  return {
    feature: getModelFeature(features, featureName),
    enabled: isModelFeatureEnabled(features, featureName, defaultEnabled),
    isExplicitlyDisabled: isModelFeatureExplicitlyDisabled(
      features,
      featureName,
    ),
    isLoading: query.isLoading,
  }
}
